import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { callCreateRoom, callJoinRoomByCode, callUpdateScreenName } from "../lib/functions";
import { DEFAULT_RULESET, type Ruleset } from "../types/game";
import { RULESET_LIST } from "../content/rules";
import { normalizeUsername, saveStoredUsername } from "../lib/username";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../lib/firebase";

const POST_AUTH_REDIRECT_KEY = "postAuthRedirectPath";

interface MatchSeat {
  uid?: string;
}

interface MatchLookupDoc {
  winner?: string | null;
  seats?: (MatchSeat | null)[];
  updatedAt?: { toMillis?: () => number };
}

export default function HomePage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [ruleset, setRuleset] = useState<Ruleset>(DEFAULT_RULESET);
  const [friendsBusy, setFriendsBusy] = useState<"create" | "join" | null>(null);
  const [friendsError, setFriendsError] = useState<string | null>(null);
  const [joinCode, setJoinCode] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(profile?.screenName ?? "");
  const [nameBusy, setNameBusy] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [resumeMatchId, setResumeMatchId] = useState<string | null>(null);
  const [resumeChecking, setResumeChecking] = useState(false);

  useEffect(() => {
    setNewName(profile?.screenName ?? "");
  }, [profile?.screenName]);

  useEffect(() => {
    const pendingPath = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    if (!pendingPath) return;
    // Only allow internal join-link handoff paths from our own app.
    if (/^\/join\/[A-Za-z0-9]{6}$/.test(pendingPath)) {
      sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
      navigate(pendingPath, { replace: true });
      return;
    }
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  }, [navigate]);

  useEffect(() => {
    const uid = user?.uid;
    if (!uid || !profile) return;
    let cancelled = false;
    async function lookupActiveMatch() {
      setResumeChecking(true);
      try {
        const matchesRef = collection(db, "matches");
        const q = query(matchesRef, where("playerUids", "array-contains", uid));
        const snap = await getDocs(q);

        let bestMatchId: string | null = null;
        let bestUpdatedAt = -1;
        for (const docSnap of snap.docs) {
          const data = docSnap.data() as MatchLookupDoc;
          const unfinished = data.winner === undefined || data.winner === null;
          const seated = (data.seats ?? []).some(seat => seat?.uid === uid);
          if (!unfinished || !seated) continue;

          const updatedAt = data.updatedAt?.toMillis?.() ?? 0;
          if (updatedAt > bestUpdatedAt) {
            bestUpdatedAt = updatedAt;
            bestMatchId = docSnap.id;
          }
        }

        if (!cancelled) {
          setResumeMatchId(bestMatchId);
        }
      } catch {
        if (!cancelled) {
          setResumeMatchId(null);
        }
      } finally {
        if (!cancelled) {
          setResumeChecking(false);
        }
      }
    }
    void lookupActiveMatch();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, profile]);

  function startQuickPlay() {
    navigate("/play", {
      state: {
        fromHomeQuickPlay: true as const,
        partnerDifficulty: "advanced",
        opponentDifficulty: "advanced",
        ruleset,
      },
    });
  }

  async function onCreateRoom() {
    setFriendsError(null);
    setFriendsBusy("create");
    try {
      const { roomId } = await callCreateRoom({ ruleset });
      navigate(`/room/${roomId}`);
    } catch (e: any) {
      console.error("createRoom failed", e);
      setFriendsError(formatCallableError(e, "Couldn't create a room."));
    } finally {
      setFriendsBusy(null);
    }
  }

  async function onJoinByCode() {
    const code = joinCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(code)) {
      setFriendsError("Please enter a 6-character code.");
      return;
    }
    setFriendsError(null);
    setFriendsBusy("join");
    try {
      const { roomId } = await callJoinRoomByCode(code);
      navigate(`/room/${roomId}`);
    } catch (e: any) {
      console.error("joinRoomByCode failed", e);
      setFriendsError(formatCallableError(e, "Couldn't join that room."));
    } finally {
      setFriendsBusy(null);
    }
  }

  function formatCallableError(e: any, fallback: string): string {
    const code = e?.code ?? e?.name;
    const msg = e?.message ?? fallback;
    return code ? `${msg} (${code})` : msg;
  }

  async function saveNameChange() {
    const nextName = normalizeUsername(newName);
    if (!nextName) {
      setNameError("Please enter a username.");
      return;
    }
    if (nextName === profile?.screenName) {
      setEditingName(false);
      setNameError(null);
      return;
    }
    setNameBusy(true);
    setNameError(null);
    try {
      saveStoredUsername(nextName);
      await callUpdateScreenName(nextName);
      setEditingName(false);
    } catch (e: any) {
      setNameError(e?.message ?? "Couldn't update your username.");
    } finally {
      setNameBusy(false);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold">
          Welcome, {profile?.screenName}
        </h1>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Link
            to="/stats"
            className="inline-flex h-9 items-center gap-1.5 rounded-full border border-emerald-400/35 bg-emerald-500/15 px-3 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-500/25"
            aria-label="View stats"
            title="Stats"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
              <path d="M4 19V9" strokeLinecap="round" />
              <path d="M10 19V5" strokeLinecap="round" />
              <path d="M16 19v-7" strokeLinecap="round" />
              <path d="M22 19H2" strokeLinecap="round" />
            </svg>
            Stats
          </Link>
          <button
            type="button"
            onClick={() => {
              setEditingName(true);
              setNameError(null);
            }}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-white/5 text-white/80 transition hover:bg-white/15"
            aria-label="Edit profile"
            title="Edit username"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
              <path d="M4 20a8 8 0 0 1 16 0" />
            </svg>
          </button>
        </div>
      </div>

      {editingName && (
        <div className="mb-4 rounded-2xl border border-white/20 bg-white/5 p-4 sm:p-5">
          <div className="mb-2 text-sm font-semibold">Change username</div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              maxLength={20}
              className="w-full rounded-lg border border-white/20 bg-black/20 px-3 py-2 outline-none focus:border-emerald-300"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveNameChange}
                disabled={nameBusy}
                className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400 disabled:opacity-50"
              >
                {nameBusy ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditingName(false);
                  setNewName(profile?.screenName ?? "");
                  setNameError(null);
                }}
                disabled={nameBusy}
                className="rounded-lg border border-white/20 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
          {nameError && <div className="mt-2 text-sm text-red-300">{nameError}</div>}
        </div>
      )}

      {!editingName && resumeMatchId && (
        <div className="mb-4 rounded-2xl border border-emerald-400/35 bg-emerald-500/10 p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-emerald-200">Resume Game</div>
              <div className="text-sm text-white/70">You have a match in progress.</div>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/play-online/${resumeMatchId}`)}
              className="rounded-lg bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-emerald-400"
            >
              Rejoin match
            </button>
          </div>
        </div>
      )}

      {!editingName && resumeChecking && (
        <div className="mb-4 text-xs text-white/50">Checking for active matches…</div>
      )}

      {/* Ruleset picker — applies to Solo Quick Play and new rooms. Once a
          match/room is created, the ruleset is locked in for that game. */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-white/50 mb-0.5">Ruleset</div>
            <div className="text-sm text-white/70">
              Applies to Solo Quick Play and new rooms you create.
            </div>
          </div>
          <Link to="/rules" className="text-xs text-emerald-300 hover:text-emerald-200 whitespace-nowrap">
            What's the difference?
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
          {RULESET_LIST.map(r => {
            const active = r.id === ruleset;
            return (
              <button
                key={r.id}
                onClick={() => setRuleset(r.id)}
                className={
                  "text-left rounded-xl border px-4 py-3 transition " +
                  (active
                    ? "bg-emerald-500/15 border-emerald-400/50 ring-1 ring-emerald-400/40"
                    : "bg-white/5 border-white/10 hover:bg-white/10")
                }
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="font-semibold">{r.shortLabel}</span>
                  {active && <span className="text-[10px] text-emerald-300 font-semibold">selected</span>}
                </div>
                <div className="text-xs text-white/60">{r.tagline}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Same-width cards on md+ — matches ruleset strip; stacked on phones. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {/* Group Play — title, copy, primary CTA to create a room. */}
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-800/20 border border-emerald-400/25 rounded-xl p-4 sm:p-5 h-full flex flex-col min-w-0">
          <div className="text-lg font-bold mb-1 flex items-center gap-2">
            <span className="text-emerald-300">▶</span> Group Play
          </div>
          <p className="text-white/65 text-sm mb-3 leading-snug flex-1">
            Create a room, share the link, and fill empty seats with bots. Bots use advanced play by default.
          </p>
          <button
            type="button"
            onClick={onCreateRoom}
            disabled={friendsBusy !== null}
            className="w-full shrink-0 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold py-2.5 rounded-lg text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {friendsBusy === "create" ? "Creating room…" : "Start Group Play"}
          </button>
        </div>

        {/* Solo Quick Play — vs bots only; advanced skills. */}
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-800/20 border border-emerald-400/25 rounded-xl p-4 sm:p-5 h-full flex flex-col min-w-0">
          <div className="text-lg font-bold mb-1 flex items-center gap-2">
            <span className="text-emerald-300">▶</span> Solo Quick Play
          </div>
          <p className="text-white/65 text-sm mb-3 leading-snug flex-1">
            Play now — partner and opponents at <span className="text-white/85">advanced</span> skill.
          </p>
          <button
            type="button"
            onClick={startQuickPlay}
            className="w-full shrink-0 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold py-2.5 rounded-lg text-sm transition"
          >
            Start Solo Quick Play
          </button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-4 sm:p-5 h-full flex flex-col min-w-0">
          <div className="text-lg font-bold mb-1 flex items-center gap-2">
            <span className="text-emerald-300">▶</span> Join With a Code
          </div>
          <p className="text-white/65 text-sm mb-3 leading-snug flex-1">
            Enter the 6-character code to join someone else’s room.
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch shrink-0">
            <input
              value={joinCode}
              onChange={e => setJoinCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="ABC123"
              className="w-full sm:flex-1 min-w-0 rounded-lg px-3 py-2 bg-white/10 border border-white/20 font-mono tracking-widest uppercase"
            />
            <button
              type="button"
              onClick={onJoinByCode}
              disabled={friendsBusy !== null}
              className="w-full sm:w-auto sm:min-w-[5.25rem] py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold text-sm disabled:opacity-50 shrink-0"
            >
              {friendsBusy === "join" ? "Joining…" : "Join"}
            </button>
          </div>
        </div>
      </div>

      {friendsError && (
        <div className="mt-4 text-red-300 text-sm">{friendsError}</div>
      )}
    </div>
  );
}
